/**
 * New / Edit WhatsApp Message Template — full-page Meta-style template builder.
 *
 * Three ways in:
 *  - `/whatsapp-templates/new` with no state → create.
 *  - `/whatsapp-templates/new` with `location.state.draft` → edit a DRAFT
 *    (prefilled, name+language locked).
 *  - `/whatsapp-templates/new` with `location.state.editTemplate`, OR the
 *    deep-link `/whatsapp-templates/:name/edit?language=…` → in-place EDIT of an
 *    existing approved/rejected/paused template (Meta re-reviews). The deep link
 *    (opened in a new tab from the template pickers) carries no router state, so
 *    the template is fetched by name+language here. This is the single path for
 *    fixing a template — including re-uploading media — replacing the old
 *    standalone fix-media screen.
 */

import React from 'react';
import { Box, Typography, IconButton, Alert, Snackbar, Tooltip, CircularProgress } from '@mui/material';
import { ArrowBack, InfoOutlined } from '@mui/icons-material';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import WhatsAppTemplateEditor from './WhatsAppTemplateEditor';
import adminApi, { WhatsAppTemplate } from '@/services/admin.api';

const WhatsAppTemplateNew: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { name: nameParam } = useParams<{ name: string }>();
  const [searchParams] = useSearchParams();
  const langParam = (searchParams.get('language') || '').trim();

  const state = location.state as
    | { draft?: WhatsAppTemplate; editTemplate?: WhatsAppTemplate }
    | null;
  const draft = state?.draft;
  const stateEdit = state?.editTemplate;

  // Deep-link edit: fetch the template by name+language when we arrived via URL
  // (no router state). Same tab navigations (list → Edit) still use router state.
  const isDeepLinkEdit = !!nameParam && !stateEdit && !draft;
  const [fetchedEdit, setFetchedEdit] = React.useState<WhatsAppTemplate | null>(null);
  const [loading, setLoading] = React.useState(isDeepLinkEdit);
  const [loadError, setLoadError] = React.useState('');

  React.useEffect(() => {
    if (!isDeepLinkEdit) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    adminApi
      .getWhatsAppTemplates()
      .then((list) => {
        if (cancelled) return;
        const found = list.find(
          (t) =>
            t.name.toLowerCase() === (nameParam || '').toLowerCase() &&
            (!langParam || t.language === langParam)
        );
        if (!found) {
          setLoadError(`Template "${nameParam}" (${langParam || 'any'}) was not found.`);
        }
        setFetchedEdit(found || null);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the template.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isDeepLinkEdit, nameParam, langParam]);

  const editTemplate = stateEdit || (isDeepLinkEdit ? fetchedEdit : undefined) || undefined;
  const source = draft || editTemplate;
  const [savedMode, setSavedMode] = React.useState<'submitted' | 'draft' | 'media' | null>(null);

  const prefix = location.pathname.startsWith('/admin') ? '/admin' : '';
  // A deep-link edit opens in its own tab with no history, so "back" goes to the
  // templates list; a state-driven edit/create can just pop the history stack.
  const goBack = () => (isDeepLinkEdit ? navigate(`${prefix}/whatsapp-templates`) : navigate(-1));

  const title = draft
    ? `Edit Draft — ${draft.name}`
    : editTemplate
      ? `Edit Template — ${editTemplate.name}`
      : 'New Message Template';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }
  if (isDeepLinkEdit && (loadError || !fetchedEdit)) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={goBack} size="small">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight="bold">
            Edit Template
          </Typography>
        </Box>
        <Alert severity="error">{loadError || 'Template not found.'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <IconButton onClick={goBack} size="small">
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>
        {editTemplate && (
          <Tooltip
            title="Editing re-submits this template to Meta for review. Its current approved version keeps sending until the edit is approved — no downtime. Name and language can’t be changed. (A same-type media swap is applied without re-approval.)"
            arrow
          >
            <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>

      <WhatsAppTemplateEditor
        onCancel={goBack}
        {...(editTemplate ? { editExisting: true } : {})}
        {...(source
          ? {
              initialTemplate: {
                name: source.name,
                language: source.language,
                category: source.category,
                components: source.components,
                ...(source.messageSendTtlSeconds
                  ? { messageSendTtlSeconds: source.messageSendTtlSeconds }
                  : {}),
              },
            }
          : {})}
        onSaved={(mode) => {
          setSavedMode(mode || 'submitted');
          setTimeout(() => goBack(), 1200);
        }}
      />

      <Snackbar open={savedMode !== null} autoHideDuration={1600}>
        <Alert severity="success">
          {savedMode === 'draft'
            ? 'Draft saved — submit it for review whenever you’re ready.'
            : savedMode === 'media'
              ? 'Media updated — no Meta review needed.'
              : 'Template submitted to Meta for review.'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppTemplateNew;
